import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService, Toast } from '../../../core/services/toast.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="fixed top-5 right-5 z-[100] flex flex-col gap-3 max-w-md w-full pointer-events-none font-sans">
      @for (toast of toastService.toasts(); track toast.id) {
        @if (toast.type === 'confirm') {
          <!-- INTERACTIVE CONFIRMATION TOAST -->
          <div
            class="pointer-events-auto p-4 rounded-2xl shadow-2xl border-2 border-amber-300/90 bg-white/95 backdrop-blur-md transition-all duration-300 transform translate-y-0 shadow-amber-500/10 animate-in slide-in-from-top-2"
          >
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-200 shrink-0 mt-0.5">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>

              <div class="flex-1 min-w-0">
                <h4 class="text-xs font-black text-slate-900 uppercase tracking-wider mb-1">
                  {{ toast.title || 'Are you sure?' }}
                </h4>
                <p class="text-xs text-slate-700 leading-relaxed font-medium">
                  {{ toast.message }}
                </p>

                <!-- Action Button Group -->
                <div class="mt-3.5 flex items-center gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    (click)="toastService.handleConfirm(toast)"
                    [ngClass]="{
                      'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-600': toast.confirmStyle === 'success',
                      'bg-rose-600 hover:bg-rose-500 text-white border-rose-600': toast.confirmStyle === 'danger',
                      'bg-amber-600 hover:bg-amber-500 text-white border-amber-600': toast.confirmStyle === 'warning',
                      'bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-600': !toast.confirmStyle || toast.confirmStyle === 'primary'
                    }"
                    class="px-3.5 py-1.5 rounded-xl text-xs font-bold border shadow-sm transition-all active:scale-95 cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <span>{{ toast.confirmText || 'Yes, Proceed' }}</span>
                  </button>

                  <button
                    type="button"
                    (click)="toastService.handleCancel(toast)"
                    class="px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 transition-all cursor-pointer"
                  >
                    {{ toast.cancelText || 'Cancel' }}
                  </button>
                </div>
              </div>

              <!-- Close button -->
              <button
                (click)="toastService.handleCancel(toast)"
                class="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded-lg hover:bg-slate-100 shrink-0 cursor-pointer"
                title="Dismiss"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        } @else {
          <!-- STANDARD NOTIFICATION TOAST -->
          <div
            class="pointer-events-auto flex items-start gap-3 p-4 rounded-2xl shadow-xl border bg-white backdrop-blur-md transition-all duration-300 transform translate-y-0"
            [ngClass]="{
              'border-emerald-300 shadow-emerald-500/10': toast.type === 'success',
              'border-rose-300 shadow-rose-500/10': toast.type === 'error',
              'border-amber-300 shadow-amber-500/10': toast.type === 'warning',
              'border-sky-300 shadow-sky-500/10': toast.type === 'info'
            }"
          >
            <!-- Icon -->
            <div class="mt-0.5 shrink-0">
              @if (toast.type === 'success') {
                <div class="w-7 h-7 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-200">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              } @else if (toast.type === 'error') {
                <div class="w-7 h-7 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-200">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              } @else if (toast.type === 'warning') {
                <div class="w-7 h-7 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-200">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              } @else {
                <div class="w-7 h-7 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center border border-sky-200">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              }
            </div>

            <!-- Content -->
            <div class="flex-1 text-xs leading-relaxed min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-bold text-slate-900 text-xs sm:text-sm">{{ toast.message }}</span>
                @if (toast.code) {
                  <span class="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold border border-slate-200">
                    {{ toast.code }}
                  </span>
                }
              </div>

              <!-- Field Validation Error Details List -->
              @if (toast.details && toast.details.length > 0) {
                <ul class="mt-2 text-xs space-y-1 bg-slate-50 border border-slate-200 rounded-xl p-2.5 font-mono text-slate-700">
                  @for (detail of toast.details; track detail) {
                    <li class="flex items-start gap-1.5">
                      <span class="text-rose-500 font-bold select-none">•</span>
                      <span class="break-all">{{ detail }}</span>
                    </li>
                  }
                </ul>
              }
            </div>

            <!-- Close button -->
            <button
              (click)="toastService.remove(toast.id)"
              class="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded-lg hover:bg-slate-100 shrink-0 cursor-pointer"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        }
      }
    </div>
  `
})
export class ToastComponent {
  toastService = inject(ToastService);
}
